class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') {
        this.close();
        return;
      }
      const addBtn = event.target.closest('.cross-sell__add-btn');
      if (addBtn) {
        event.preventDefault();
        this.handleCrossSellAdd(addBtn);
      }
    });
    this.addEventListener('change', (event) => {
      const toggle = event.target.closest('.cart-subscription-toggle');
      if (toggle) this.handleSubscriptionToggle(toggle);
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.open();
    });

    this.classList.toggle('is-empty', false);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  refreshDrawer() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = this.querySelector('.drawer__inner');
        const source = doc.querySelector('.drawer__inner');
        if (inner && source) inner.innerHTML = source.innerHTML;

        const sourceCartDrawer = doc.querySelector('cart-drawer');
        if (sourceCartDrawer) {
          this.classList.toggle('is-empty', sourceCartDrawer.classList.contains('is-empty'));
        }
      })
      .then(() => fetch(`${routes.cart_url}?section_id=cart-icon-bubble`))
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const bubble = document.getElementById('cart-icon-bubble');
        const source = doc.querySelector('.shopify-section');
        if (bubble && source) bubble.innerHTML = source.innerHTML;
      });
  }

  handleSubscriptionToggle(toggle) {
    toggle.disabled = true;
    const lineKey = toggle.dataset.lineKey;
    const sellingPlan = parseInt(toggle.dataset.sellingPlan, 10);

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, selling_plan: sellingPlan }),
    })
      .then((r) => r.json())
      .then(() => this.refreshDrawer())
      .catch((e) => {
        console.error('Subscription toggle error:', e);
        toggle.disabled = false;
        toggle.checked = false;
      });
  }

  handleCrossSellAdd(btn) {
    btn.disabled = true;
    const originalText = btn.dataset.buttonText || btn.textContent;
    btn.textContent = 'ADDING...';
    const variantId = parseInt(btn.dataset.variantId, 10);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((r) => r.json())
      .then(() => this.refreshDrawer())
      .catch((e) => {
        console.error('Cross-sell add error:', e);
        btn.disabled = false;
        btn.textContent = originalText;
      });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  onCartUpdate() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = document.querySelector('#CartDrawer .drawer__inner');
        const source = doc.querySelector('.drawer__inner');
        if (inner && source) inner.innerHTML = source.innerHTML;

        const cartDrawerEl = document.querySelector('cart-drawer');
        const sourceEl = doc.querySelector('cart-drawer');
        if (cartDrawerEl && sourceEl) {
          cartDrawerEl.classList.toggle('is-empty', sourceEl.classList.contains('is-empty'));
        }
      })
      .then(() => fetch(`${routes.cart_url}?section_id=cart-icon-bubble`))
      .then((r) => r.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const bubble = document.getElementById('cart-icon-bubble');
        const source = doc.querySelector('.shopify-section');
        if (bubble && source) bubble.innerHTML = source.innerHTML;
      })
      .catch((e) => console.error('Cart update error:', e));
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
